import {
    useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import framework from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import SignInEN from 'pages/Auth/locales/SignIn_en_US';
import SignInFR from 'pages/Auth/locales/SignIn_fr_CA';
import {
    colors, fontSizes, fontWeights, shadows, space
} from 'style/config';
import useAuthFlow from 'pages/Auth/hooks/useAuthFlow';
import useOtpSession from 'pages/Auth/SignIn/hooks/useOtpSession';
import useAuthContextRecovery from 'pages/Auth/SignIn/hooks/useAuthContextRecovery';
import useOtpSessionRecovery from 'pages/Auth/SignIn/hooks/useOtpSessionRecovery';
import usePasskeyDetection from 'pages/Auth/SignIn/hooks/usePasskeyDetection';
import useCredentialLookup from 'pages/Auth/SignIn/hooks/useCredentialLookup';
import CredentialEntryScreen from 'pages/Auth/SignIn/CredentialEntryScreen.f';
import AuthorizationScreen from 'pages/Auth/SignIn/AuthorizationScreen.f';
import OtpEntryScreen from 'pages/Auth/SignIn/OtpEntryScreen.f';
import RiskStepUpOtpScreen from 'pages/Auth/SignIn/RiskStepUpOtpScreen.f';
import Logo from 'components/Logo/Logo.f';
import LegalFooter from 'components/Footer/LegalFooter/LegalFooter.f';
import { mediaQueries } from 'style/config';
import store from 'Store';
import userUtils from 'utils/User';
import UserActions from 'actions/UserActions';
import Actions from 'Actions';
// SIM-24 (ACS-4418): page-based passkey parity + Option 2 suppression signal.
import { classifyPasskeyError, logPasskeyForDynatrace } from 'utils/passkeyDynatrace';
import { passkeySignInLock } from 'constants/passkeys';
import { showPasskeyFailureModalIfNeeded } from 'utils/passkeyFailureModal';

const { wrapFunctionalComponent } = framework;
const getText = LanguageLocaleUtils.getLocalisedDataFromResource(SignInEN, SignInFR);

const noop = () => {};

// Figma desktop horizontal gutter for the header + footer (px-96). Applied as a
// fixed inset (not a centered Container) so the logo/footer stay at 96px on any
// wide viewport. Mobile uses the 16px space[4] gutter.
const SIDE_GUTTER_DESKTOP = 96;

// The dark-scrim in-flight Loader for the SIM-7 sign-in flow (Figma 270-85083): the
// SAME shared /img/loader.gif spinner as the rest of the app, but on a 25%-black
// scrim (colors.darken3 = rgba(0,0,0,.25), the same overlay token Modal uses)
// instead of the global interstice's 75%-white one. Rendered with hasBg={false}
// inside a fixed full-screen overlay div that paints the dark scrim, so the global
// Interstice is left untouched and this treatment is scoped to sign-in only (FIX 3).

// useOtpSessionRecovery persists a canonical mfaMode value ('phone' | 'email') in its
// whitelisted `method` field (in this flow otpSession.method === mfaMode). On an OTP
// refresh we derive the credentialType from it so OtpEntryScreen's F-001 email
// decision is correct without persisting PII.
const credentialTypeForMode = mfaMode => (mfaMode === 'phone' ? 'PHONE' : 'EMAIL');

/**
 * Auth/SignIn page shell (SIM-1 / ACS-4125).
 *
 * Renders the page landmark, focused H1 heading, and a screen-slot placeholder
 * that reflects the current step from useAuthFlow. Downstream stories will
 * render actual sign-in screens into this slot; per ADR-TS-001 this shell must
 * NOT import the deprecated modal form.
 *
 * SIM-2 (ACS-4126): renders CredentialEntryScreen into the slot when the flow is
 * on the 'credential' screen.
 * SIM-7 (ACS-4131): renders AuthorizationScreen ('authorization') and
 * OtpEntryScreen ('otp'); the lookup result is carried into authContext by
 * CredentialEntryScreen (F-002) and the OTP session lives in useOtpSession.
 */
// ACS-4386: OTP/auth recovery must apply ONLY to a genuine in-place REFRESH of the
// sign-in page, not to a fresh arrival at /signin (link, typed URL, or browser Back
// out-of-and-into the flow). Leaving /signin is a full-page (hard) navigation here, so
// there is no React unmount to clear the persisted sessionStorage — instead we gate
// restoration on the navigation type: only a `reload` restores the OTP/authorization
// step; any other arrival (`navigate` / `back_forward`) starts fresh at credential
// entry and clears the stale recovery state. SSR-safe: only called inside a mount
// effect (never at module scope). Falls back to the deprecated performance.navigation
// when the Navigation Timing L2 entry is unavailable; defaults to false (start fresh).
const isPageReload = () => {
    try {
        const [nav] = window.performance.getEntriesByType('navigation');

        if (nav && nav.type) {
            return nav.type === 'reload';
        }

        // Legacy fallback: performance.navigation.type === 1 is TYPE_RELOAD.
        return window.performance.navigation?.type === 1;
    } catch (e) {
        return false;
    }
};

// Derive the masked destination for a chosen method — masked phone from the
// last-4 only (BR-021), or the already-masked email from the lookup.
const maskedDestinationFor = (mfaMode, authContext) => {
    if (mfaMode === 'phone') {
        return `(***) ***-${authContext?.phoneLast4Digits}`;
    }

    return authContext?.maskedEmail;
};

// AC-023 accessible-name anchor: the id that <main aria-labelledby> points at and
// that route-entry focus lands on. Per-screen heading ownership (Figma) — screens
// whose top heading IS "Sign In" (credential / passkey-detection / authorization)
// let the page shell render the h1; the OTP screen (Figma 270-84837 has NO "Sign In"
// heading) OWNS its own top heading instead, carrying this same id so <main> stays
// labelled and the OTP heading is the focus/label target.
const HEADING_ID = 'signin-heading';

// Screens whose Figma top heading is the page title "Sign In": the shell renders the
// h1 for these. The OTP screen is intentionally absent — it renders its own heading.
// (The password branch is an INLINE state on the authorization screen — no separate
// screen — so 'password' is not listed here; the authorization heading applies.)
// SIM-9 (Figma 1466-19076): 'disambiguation' is the copy-only phone-disambiguation
// mode of CredentialEntryScreen — the shell renders the same "Sign In" heading.
const SHELL_HEADING_SCREENS = ['credential', 'passkey-detection', 'authorization', 'disambiguation'];

const SignIn = () => {
    // Single source of truth for the auth-flow state machine. `goTo` is passed
    // down to the mounted screen so navigation updates THIS instance's
    // currentScreen (the one the slot renders against) — screens must not call
    // useAuthFlow() themselves.
    //
    // SIM-24 (ACS-4418) AC-3: `redirectToReturnUrl` is exposed here so the passkey success
    // callbacks below reuse the exact same seam OTP/password paths use inside
    // completeAuthentication — one useReturnUrl instance per page render (no drift, no
    // duplicate sessionStorage writes).
    const {
        currentScreen, goTo, completeAuthentication, authContext, setAuthContext, redirectToReturnUrl
    } = useAuthFlow();
    // Single OTP session for the flow; seeded on method selection and consumed by
    // OtpEntryScreen. Counters persist across method switches (BR-041).
    const otpSession = useOtpSession();
    // AC-AUTH-REFRESH (SIM-7): persist/recover/clear the non-PII authorization-step
    // context so a refresh re-renders the Authorization step (not credential entry).
    const authRecovery = useAuthContextRecovery();
    // SIM-10 OTP recovery (FIX 6): survives a refresh ON THE OTP STEP so the sent code
    // stays valid and is NOT re-sent (BR-028). We use ONLY its pure persist/recover/
    // clear helpers and neutralise its built-in mount effect by passing a no-op
    // navigator — precedence (OTP > authorization > credential) is orchestrated in the
    // single mount effect below so the two recovery mechanisms compose instead of
    // racing. SIM-10's own hook + tests are left completely untouched.
    const otpRecovery = useOtpSessionRecovery(noop);
    // in-flight loader state (FIX 3/7): true while an OTP send/verify is in flight and
    // kept true across the post-verify redirect; drives the dark-scrim shared Loader.
    const [busy, setBusy] = useState(false);
    // Set when the mount effect restored the OTP step from recovery: tells OtpEntryScreen
    // to SKIP its initial send (the previously-sent code stays valid — BR-028, no resend).
    const otpRestoredRef = useRef(false);
    // SIM-24 AC-11 (Option 2 suppression contract, ADR-TS-011): strict `=== '1'` check,
    // computed once on mount from the URL. When true, the /signin page skips ALL passkey
    // prompts on this navigation (SINGLE auto-signin, MULTIPLE selector) and the URL-strip
    // effect below removes the flag from the URL bar so a bookmark / share / reload does not
    // permanently disable passkey detection on that URL.
    const passkeyOfferedSuppressed = useMemo(() => {
        if (typeof window === 'undefined') {
            return false;
        }

        return new URLSearchParams(window.location.search).get('passkeyOffered') === '1';
    }, []);
    // SIM-24 AC-11: skip the Beyond Identity SDK preflight when the trigger already prompted
    // and the user declined — the detection UI on /signin would just re-ask for the same passkey
    // they just walked away from. Also saves the ~SDK-load latency on the suppressed visit.
    const {
        status: passkeyStatus,
        passkeys: detectedPasskeys,
        DETECTION_STATUS
    } = usePasskeyDetection({
        skip: passkeyOfferedSuppressed
    });
    // ACS-4437 (Capability A): auto-lookup seam for the recognized-user auto-route AND the
    // registration-redirect credential-carry, reusing the SAME lookup CredentialEntryScreen
    // uses (useCredentialLookup.js:48-66) so both stories share one code path. Consumed in
    // the passkey-detection effect's NONE branch (SIM-24 preflight had no opinion on NONE
    // beyond `goTo('credential')`, so the two stories compose cleanly here).
    const { lookup } = useCredentialLookup();
    const headingRef = useRef(null);
    const hasTriggeredSelector = useRef(false);
    // BR-016 / AC-022 (ADR-SIM9-003, resolved OQ-1 = LOGGED_IN only): set on mount
    // when a fully-authenticated customer was auto-logged-out, so the recovery effect
    // below does NOT restore a stale authorized step onto the fresh sign-in shell.
    const loggedOutOnMountRef = useRef(false);

    // BR-016 auto-logout guard (Figma-less, PRD prd.v2.md:380). A fully LOGGED_IN
    // customer landing on /signin is silently signed out — NO confirmation modal and
    // NO redirect — then shown a fresh sign-in shell. signOut(false, true, true)
    // skips both the confirmation modal and the redirect while running the logout API
    // + session cleanup (UserActions.js:1793). RECOGNIZED / RECOGNIZED_SSI users are
    // left alone (they have no active authenticated session). SSR-safe: the guard runs
    // only inside this browser mount effect, never at module scope (SIM9-P4-05). It is
    // ordered BEFORE the recovery effect so a logout resets to a fresh flow and the
    // recovery restore cannot fight it.
    useEffect(() => {
        if (userUtils.isSignedIn()) {
            loggedOutOnMountRef.current = true;
            store.dispatch(UserActions.signOut(false, true, true));
            // Clear any persisted authorization/OTP recovery so the fresh shell starts
            // at credential entry, not a stale authorized step (SIM9-P4-03).
            authRecovery.clear();
            otpRecovery.clear();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (headingRef.current) {
            headingRef.current.focus();
        }
    }, []);

    // OURS SIM-24 AC-11: strip `passkeyOffered` from the URL bar after reading it into
    // passkeyOfferedSuppressed above. Without this, a refresh / bookmark / share of the
    // current URL would permanently suppress passkey detection on that URL. Wrapped in a
    // useEffect so it is SSR-safe and only runs once on mount (post-hydration).
    useEffect(() => {
        if (!passkeyOfferedSuppressed || typeof window === 'undefined') {
            return;
        }

        try {
            const url = new URL(window.location.href);
            url.searchParams.delete('passkeyOffered');
            const query = url.searchParams.toString();
            const nextUrl = url.pathname + (query ? `?${query}` : '') + url.hash;
            window.history.replaceState({}, '', nextUrl);
        } catch (e) {
            // Best-effort: leaving the flag in place is not a security regression, just a
            // UX one (passkey stays suppressed on this URL). Never throw from a mount effect.
        }
    }, [passkeyOfferedSuppressed]);

    // OURS SIM-24 AC-11 (page-level parity with the trigger's redirectWithSuppression): when
    // the user cancels/declines a passkey attempt that fired ON /signin (SINGLE auto-signin
    // or the MULTIPLE selector), stamp `passkeyOffered=1` into the current URL via
    // replaceState so a subsequent refresh does NOT re-fire the same auto-signin. The trigger
    // site accomplishes this by doing a full navigation to `/signin?passkeyOffered=1`; here
    // we are already on /signin, so we achieve the same suppression contract in-place without
    // a page reload (returnUrl and marketing params are preserved verbatim).
    //
    // The URL-strip effect above only fires on mount (guarded by the useMemo-captured
    // passkeyOfferedSuppressed), so a post-mount write here is stable — it will not be
    // undone by React re-renders of the current shell instance.
    const markPasskeyOfferedInUrl = useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }

        try {
            const url = new URL(window.location.href);
            url.searchParams.set('passkeyOffered', '1');
            const query = url.searchParams.toString();
            const nextUrl = url.pathname + (query ? `?${query}` : '') + url.hash;
            window.history.replaceState({}, '', nextUrl);
        } catch (e) {
            // Best-effort: failure to mark the URL is a UX regression (refresh re-prompts),
            // not a correctness issue. Never throw from a cancel handler.
        }
    }, []);

    // THEIRS SIM-17 (ACS-4141) DEFER-2b: strip the pre-seeded `credential` URL param once it
    // has been consumed. The credential carry itself (account-closure / guest-checkout
    // redirect and ACS-4437's registration-redirect) is read + seeded into authContext by the
    // passkey-detection flow below (goTo('credential', { enteredCredential, autoSubmit })).
    // Here we only remove the raw value from the URL (preserving returnUrl + other params) so
    // the PII does not linger in browser history / referrers / client-side logging (Copilot
    // review, PR #10087). Deferred to a macrotask so it runs AFTER the seed -> auto-submit
    // render commits (a synchronous replaceState races the downstream auto-submit); the timer
    // is cleared on unmount. SSR-safe: browser-only mount effect.
    //
    // OURS × THEIRS composition note: this effect fires independently of OURS's
    // passkeyOffered URL-strip above (different param, different guard). The setTimeout(..,0)
    // is load-bearing — the passkey-detection effect further below reads `?credential=X` in
    // both its NONE branch (ACS-4437 credential-carry auto-submit) AND OURS's SINGLE/MULTIPLE
    // composition rule (skip WebAuthn when identity was specified). Deferring the strip to a
    // macrotask lets those synchronous readers see the param first; the timer clears on
    // unmount so a fast-back-nav does not fire the strip against a stale URL.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        if (!params.has('credential')) {
            return undefined;
        }

        params.delete('credential');
        const nextSearch = params.toString();
        const nextUrl = window.location.pathname + (nextSearch ? `?${nextSearch}` : '') + window.location.hash;
        const stripTimer = setTimeout(() => {
            window.history.replaceState(window.history.state, '', nextUrl);
        }, 0);

        return () => clearTimeout(stripTimer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refresh recovery on mount. Precedence is OTP > authorization > credential:
    //   - If SIM-10 OTP-recovery state exists, OTP WINS: rehydrate the otpSession +
    //     authContext (masked, non-PII) and render the OTP step WITHOUT a re-send.
    //   - Else if authorization-recovery state exists, restore the (non-PII, masked)
    //     authContext and render the Authorization step WITHOUT a new lookup/OTP send.
    //   - Else fall through to the default flow (credential entry), untouched.
    useEffect(() => {
        // BR-016: a fresh sign-in after auto-logout must NOT restore a stale
        // authorized step (SIM9-P4-03) — skip recovery entirely in that case.
        if (loggedOutOnMountRef.current) {
            return;
        }

        // ACS-4386: persistence is scoped to the auth pages. Only a genuine in-place
        // REFRESH restores the OTP/authorization step (BR-028: sent code stays valid).
        // Arriving at /signin any other way — a link, a typed URL, or the browser Back
        // button after having left the flow — must NOT re-open a stale OTP screen, so we
        // clear the persisted recovery state and fall through to credential entry.
        // (Leaving /signin is a full-page navigation, so there is no unmount to clear it.)
        if (!isPageReload()) {
            otpRecovery.clear();
            authRecovery.clear();

            return;
        }

        const otpRecovered = otpRecovery.recover();

        if (otpRecovered) {
            // FIX 6 — restore the OTP step. Only non-PII fields survive a refresh
            // (BR-021): the masked destination, the resend counters, and the canonical
            // mfaMode carried in `method`. The raw credential, otpCode and, per the
            // SIM-10 precedent, the securityToken are NOT persisted.
            //
            // LIMITATION (securityToken): OTP VERIFY needs the securityToken, which is
            // intentionally never persisted (PII-adjacent, SIM-10 whitelist). After an
            // OTP refresh we therefore restore the OTP SCREEN — masked destination shown,
            // the sent code still valid, code-entry available, NO auto-resend — but a
            // verify attempt cannot complete until a token is re-established. This matches
            // SIM-10's intent ('the sent code stays valid') and defers full post-refresh
            // verify to a follow-up that designs token re-hydration without storing PII.
            const restoredMode = otpRecovered.method;
            const restoredMasked = otpRecovered.maskedDestination;
            otpSession.reset({
                method: restoredMode,
                mfaMode: restoredMode,
                maskedDestination: restoredMasked
            });
            setAuthContext({
                mfaMode: restoredMode,
                maskedDestination: restoredMasked,
                credentialType: credentialTypeForMode(restoredMode)
            });
            otpRestoredRef.current = true;
            goTo('otp');

            return;
        }

        const recovered = authRecovery.recover();

        if (recovered && Array.isArray(recovered.availableAuthMethods)) {
            // Restored context is MASKED-only: the raw enteredCredential (PII) was
            // never persisted (BR-021), so it is absent and AuthorizationScreen falls
            // back to the masked credential display.
            setAuthContext(recovered);
            goTo('authorization');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persist the non-PII authorization subset whenever we are on the authorization
    // step (covers both the normal advance from credential entry and a re-entry), so
    // a subsequent refresh can restore it. The hook whitelists fields and drops PII.
    useEffect(() => {
        if (currentScreen === 'authorization' && authContext) {
            authRecovery.persist(authContext);
        }
    }, [currentScreen, authContext, authRecovery]);

    // Persist the non-PII OTP subset whenever we are on the OTP step (FIX 6) so a
    // refresh restores the OTP step (not credential entry). The whitelist carries the
    // masked destination, resend counters and the canonical mfaMode (in `method`) —
    // never the raw credential, otpCode or securityToken (BR-021).
    useEffect(() => {
        if (currentScreen === 'otp' && authContext) {
            otpRecovery.persist({
                method: authContext.mfaMode,
                maskedDestination: authContext.maskedDestination,
                resendCount: otpSession.otpSession.resendCount,
                cooldownEndTime: otpSession.otpSession.cooldownEndTime
            });
        }
    }, [currentScreen, authContext, otpSession.otpSession.resendCount, otpSession.otpSession.cooldownEndTime, otpRecovery]);

    // Method selection from AuthorizationScreen. Only OTP methods (phone/email)
    // arrive here — the password branch is handled INLINE on AuthorizationScreen
    // itself (Figma 270-87274), so it never calls onSelectMethod. An OTP method
    // seeds the session's mfaMode/destination and advances to OTP entry, where
    // the initial send fires (send logic stays in one place).
    const handleSelectMethod = useCallback(
        mfaMode => {
            // SIM-8 / ACS-4132: password is NOT routed here. AuthorizationScreen owns
            // the inline password branch (handlePasswordSubmit → authenticationApi.login,
            // Figma 270-87274) and calls completeAuthentication itself. This handler
            // only seeds an OTP session (phone|email) and advances to the OTP screen.
            const maskedDestination = maskedDestinationFor(mfaMode, authContext);
            setAuthContext({ ...authContext, mfaMode, maskedDestination });
            otpSession.reset({ method: mfaMode, mfaMode, maskedDestination });
            // Advancing to OTP is a fresh, user-initiated send — NOT a refresh restore.
            otpRestoredRef.current = false;
            // Advancing to OTP: clear authorization-recovery so it can't override the
            // OTP step on a subsequent refresh (precedence + no stale authorization
            // state once the user has moved past it).
            authRecovery.clear();
            goTo('otp');
        },
        [authContext, otpSession, goTo, setAuthContext, authRecovery, completeAuthentication]
    );

    // Navigation handler passed to AuthorizationScreen: leaving the authorization
    // step back to credential entry (Edit / back) clears the authorization-recovery
    // state so a refresh on credential entry does NOT bounce forward to authorization.
    // MUST forward the optional contextPatch (ADR-SIM8-001): the password → risk-step-up
    // handoff calls goTo('risk-step-up', { mfaMode, phoneLast4Digits, credential }); if the
    // patch were dropped here, authContext.mfaMode would stay unset and RiskStepUpOtpScreen
    // would always default to the email channel (even for a phone step-up).
    const goToFromAuthorization = useCallback(
        (screen, contextPatch) => {
            if (screen === 'credential') {
                authRecovery.clear();
            }

            goTo(screen, contextPatch);
        },
        [goTo, authRecovery]
    );

    // Per-screen heading ownership: the shell renders the "Sign In" h1 only for the
    // screens whose Figma top heading is the page title. On the OTP screen the shell
    // h1 is NOT rendered — OtpEntryScreen owns its own top heading (carrying the same
    // HEADING_ID so <main aria-labelledby> still resolves and focus can land on it).
    const showShellHeading = SHELL_HEADING_SCREENS.includes(currentScreen);
    // SIM-24 (ACS-4418) page-based passkey parity. This useEffect drives ALL three page-based
    // passkey paths (was: only MULTIPLE selector, with a no-op successCallback and no SINGLE
    // branch — the pre-SIM-24 partial-by-accident state).
    //
    //   AC-11 (suppression)  : `passkeyOffered=1` in the URL means the user just declined a
    //                          passkey attempt at the trigger. Skip everything (no auto, no
    //                          selector) and go straight to credential entry. The URL-strip
    //                          effect above removes the flag from the URL bar so a refresh /
    //                          bookmark / share does not permanently disable passkey detection.
    //   AC-1  (SINGLE auto)  : exactly one passkey on device -> dispatch UserActions.signIn
    //                          with passkey extraParams + suppressErrorModal (BR-009), then
    //                          redirectToReturnUrl on success. Silent fallback to credential
    //                          entry on failure/decline. passkeySignInLock is held around the
    //                          dispatch to keep any background 401 from opening a competing
    //                          manual sign-in prompt (ufeApi/newAuthUfeApi both check it).
    //   AC-3/AC-4 (MULTIPLE) : shared PasskeySelectorModal (unchanged). successCallback now
    //                          calls redirectToReturnUrl (was: no-op — the redirect bug this
    //                          story exists to fix). errback (dismiss / close / "sign in with
    //                          another account") falls through to the credential entry screen.
    //
    // AC-6 gate consistency: passkeyStatus already respects isPasskeyEnabled internally
    // (usePasskeyDetection.js), and /signin is route-gated on isNewSignInFlowEnabled (SIM-10).
    // No new gate check needed here.
    useEffect(() => {
        if (currentScreen !== 'passkey-detection' || hasTriggeredSelector.current) {
            return undefined;
        }

        // OURS SIM-24 AC-11 × THEIRS ACS-4437 composition: the `passkeyOffered=1` URL flag
        // is consumed by `usePasskeyDetection({ skip: passkeyOfferedSuppressed })` above —
        // when the flag is set, the hook resolves `passkeyStatus = NONE` immediately, so
        // the SINGLE / MULTIPLE branches below are naturally skipped (no re-prompt for the
        // passkey the user already declined at the trigger). The NONE branch still runs on
        // suppression, which is CORRECT for ACS-4437: RU-T-08 asserts that a recognized user
        // arriving with `passkeyOffered=1` MUST still get the recognized-user auto-route
        // (friction reduction is orthogonal to passkey-decline suppression). An earlier draft
        // of this resolution short-circuited to `goTo('credential')` here on suppression,
        // which regressed RU-T-08 by preventing the auto-route from ever firing — removed.
        //
        // OURS SIM-24 (ACS-4418) code-review #8 / audit F-020: unmount / re-run cancellation
        // flag. WebAuthn interaction can take multiple seconds while the user provides
        // biometric. If the SignIn shell unmounts in that window (the user leaves /signin —
        // back button, full-page navigation triggered elsewhere, or React re-runs this
        // effect after deps change) the SINGLE .then/.catch below MUST NOT invoke navigation
        // / URL-write / modal side-effects against an unmounted component — that would either
        // redirect the user off their new destination (`.then` → `redirectToReturnUrl` yanks
        // them back to the stale returnUrl) or write `passkeyOffered=1` to a URL they no
        // longer control. THEIRS's NONE-branch async lookup below ALSO reads this via closure
        // so a late-arriving lookup response cannot goTo/setAuthContext on the stale shell.
        // The cleanup at the bottom flips this to true; each branch reads it via closure.
        // Dynatrace logging still fires regardless (observability of what happened); lock
        // release still fires regardless (leaking would poison the next page's 401 defer).
        let cancelled = false;

        // THEIRS ACS-4437 NONE-branch: recognized-user auto-route + registration-redirect
        // credential carry + fallthrough. Composes cleanly with OURS SIM-24 because SIM-24's
        // preflight was a no-op on NONE (`goTo('credential')` only). Runs BEFORE the SINGLE /
        // MULTIPLE credential-carry composition rule below so a NONE visit with `?credential=X`
        // is treated as "carry the credential to auto-submit" (the ACS-4437 contract), not as
        // "passkey-skipped candidate" (which is nonsensical on NONE anyway).
        if (passkeyStatus === DETECTION_STATUS.NONE) {
            // ACS-4437 / RU-1..RU-3 (AC-1): recognized-user auto-route.
            if (userUtils.isRecognized()) {
                // Copilot ℹ️: trim once and use the trimmed value everywhere (guard, lookup,
                // enteredCredential) so stray whitespace in the hydrated profile email can't
                // cause a surprising lookup failure.
                const email = (userUtils.getProfileEmail() || '').trim();

                if (email.length > 0) {
                    // useEffect cannot be async directly — wrap the lookup in an inner async
                    // function (AC-1/AC-7).
                    const attemptAutoRoute = async () => {
                        const result = await lookup(email, 'EMAIL');

                        // OURS cancellation guard: a late-arriving lookup response must not
                        // drive goTo / setAuthContext on an unmounted / re-run shell.
                        if (cancelled) {
                            return;
                        }

                        if (result && result.securityToken && !result.error && !result.requiresEmailConfirmation) {
                            // AC-3: maskedEmail (masked) travels in authContext for display;
                            // enteredCredential carries the RAW email for the password path
                            // (BR-021 — never the reverse).
                            setAuthContext({
                                securityToken: result.securityToken,
                                availableAuthMethods: result.availableAuthMethods,
                                phoneLast4Digits: result.phoneLast4Digits,
                                maskedEmail: result.maskedEmail,
                                enteredCredential: email,
                                credentialType: 'EMAIL'
                            });
                            goTo('authorization');
                        } else {
                            // AC-7: silent fallback — no error modal, no dead-end. Covers
                            // lookup failure AND disambiguation (which requires user
                            // interaction and cannot be auto-routed).
                            goTo('credential');
                        }
                    };

                    attemptAutoRoute();

                    return () => {
                        cancelled = true;
                    };
                }
            }

            // ACS-4437 / RU-4..RU-6 (AC-4): registration-redirect credential carry.
            // Not recognized (or recognized with no hydrated email yet) — check for a
            // `?credential=` param carried from the Register "already registered" redirect
            // (ADR-TS-013) before falling through to plain credential entry.
            const noneUrlParams = new URLSearchParams(window.location.search);
            // Copilot ℹ️: trim once and carry the trimmed value so stray whitespace in the
            // redirect param can't cause a surprising lookup failure on auto-submit.
            const noneCredentialParam = (noneUrlParams.get('credential') || '').trim();

            if (noneCredentialParam.length > 0) {
                // Carry the credential via `enteredCredential` (→ CredentialEntryScreen's
                // `initialCredential` seed) paired with `autoSubmit`, unifying with the
                // SIM-17/ACS-4141 auto-submit contract so both stories share one code path.
                goTo('credential', {
                    enteredCredential: noneCredentialParam,
                    autoSubmit: true
                });

                return () => {
                    cancelled = true;
                };
            }

            // Fallthrough (parity with SIM-24 pre-merge): no recognized user, no credential
            // param → plain credential entry.
            goTo('credential');

            return () => {
                cancelled = true;
            };
        }

        // ACS-4437 × SIM-24 composition contract (SINGLE / MULTIPLE): if the /signin URL
        // carries `?credential=X`, the caller (RegisterEmailLookup "already registered"
        // → Authentication.navigateToSignIn(undefined, { credential })) has specified the
        // identity. Opportunistically firing WebAuthn for a device passkey held by a
        // DIFFERENT identity would silently defeat ACS-4437's credential-carry (and, on a
        // shared device, sign the current visitor in as the wrong person). Skip passkey UI
        // and go straight to credential entry with autoSubmit — mirrors the trigger-site
        // skip in Authentication.js:_navigateToSignInImpl when `extraParams.credential` is
        // set, so trigger and destination are consistent (extraParams.credential at trigger
        // ⇒ redirectPlain to /signin?credential=X ⇒ this branch fires here).
        //
        // NOTE: this rule preserves both stories' intents rather than adding new behavior.
        // Without it, SIM-24's SINGLE path would fire WebAuthn for whoever's on device and
        // silently break ACS-4437's credential-carry on any device with a passkey.
        const singleMultipleUrlParams = new URLSearchParams(window.location.search);
        const singleMultipleCredentialParam = (singleMultipleUrlParams.get('credential') || '').trim();

        if (singleMultipleCredentialParam.length > 0) {
            hasTriggeredSelector.current = true;
            goTo('credential', {
                enteredCredential: singleMultipleCredentialParam,
                autoSubmit: true
            });

            return () => {
                cancelled = true;
            };
        }

        if (passkeyStatus === DETECTION_STATUS.SINGLE) {
            // AC-1 + AC-3: parity with the legacy modal's autoSignInIfSinglePasskey. Dispatch
            // shape matches PasskeySelectorModal's (isPasskeyLogin + credential ids +
            // suppressErrorModal: true). Guard hasTriggeredSelector so the effect never fires
            // twice even if React re-runs it on a status change.
            //
            // IMPORTANT — outcome observation via callbacks, not `await dispatch`:
            //   UserActions.signIn's thunk does NOT return a promise (line 1795-1799: it just
            //   calls `fingerPrint.setupFingerprint(executeLogin)` which is fire-and-forget),
            //   AND its inner .catch swallows the passkey rejection (line 1743) so it never
            //   propagates back through dispatch either. `await store.dispatch(...)` would
            //   therefore resolve to undefined IMMEDIATELY — before WebAuthn even prompts —
            //   causing redirectToReturnUrl() to fire pre-authentication and the catch to
            //   never run on cancel. The only reliable outcome signal is the positional
            //   successCallback (arg 5) / failureCallback (arg 6) that UserActions.signIn
            //   invokes from its internal .then/.catch. Promise-wrap them here.
            //
            // passkeySignInLock: held while sign-in is in flight so a background 401 handled
            // by ufeApi / newAuthUfeApi defers its manual sign-in prompt for the lock's hold
            // window instead of racing us with a competing modal. Released in the shared
            // .finally so the lock spans the inline post-settlement work (redirect kick-off on
            // success, modal dispatch on failure) — parity with PasskeySelectorModal.f.jsx's
            // outer try/catch/finally around capturedSuccessCallback and the failure-modal call.
            hasTriggeredSelector.current = true;

            const [passkey] = detectedPasskeys;

            passkeySignInLock.acquire();

            // Local navigation helper reused by success (.then) and by both fallback paths in
            // .catch (silent path via onDismiss AND defensive .catch on the modal helper for
            // dynamic-import failure — code-review #2 / audit F-009). Defined here so the
            // three call sites cannot drift. Guards on `cancelled` so a late onDismiss from a
            // stacked failure-modal cannot drive goTo on an unmounted shell (code-review #8).
            const goToCredential = () => {
                if (cancelled) {
                    return;
                }

                goTo('credential');
            };

            new Promise((resolve, reject) => {
                store.dispatch(
                    UserActions.signIn(
                        passkey?.identity?.emailAddress,
                        undefined,
                        true,
                        false,
                        () => resolve(),
                        reason => reject(reason || new Error('Passkey sign-in failed')),
                        false,
                        null,
                        false,
                        false,
                        {},
                        {
                            isPasskeyLogin: true,
                            passkeyId: passkey.passkeyId,
                            passkeyCredentialId: passkey.id,
                            identityId: passkey?.identity?.id,
                            suppressErrorModal: true
                        }
                    )
                );
            })
                .then(() => {
                    // Code-review #8: if we unmounted / re-ran while WebAuthn was in flight, do
                    // NOT redirect — that would yank the user off wherever they intentionally
                    // navigated to. Lock still releases via .finally below.
                    if (cancelled) {
                        return;
                    }

                    // AC-3: honor the returnUrl. redirectToReturnUrl comes from useAuthFlow
                    // (single useReturnUrl instance) — passkey routes here directly instead of
                    // through completeAuthentication because UserActions.signIn is already
                    // dispatched (ADR-TS-010).
                    redirectToReturnUrl();
                })
                .catch(async error => {
                    // SIM-24 (ACS-4418) F-001: login.js attaches operation/category to the synthesized
                    // rejection when the failure came through its passkey path. Use those when present so
                    // Dynatrace sees Biometric/API/SDK correctly categorized; fall back to local classify
                    // for rejections that bypassed login.js (e.g. synchronous store.dispatch throw).
                    // Observability fires REGARDLESS of cancellation — we want the Dynatrace signal
                    // even if the user left the page (helps triage "why is passkey failing" queries).
                    logPasskeyForDynatrace(error?.category || classifyPasskeyError(error), error?.operation || 'AutoSignIn', error, 'signin-page');

                    // Code-review #8: everything below has USER-VISIBLE side effects (URL write,
                    // modal dispatch, navigation). Skip if we unmounted / re-ran — writing the
                    // passkeyOffered flag to a URL the user no longer controls is a real UX defect
                    // (bookmark pollution) and rendering a modal on a foreign page is worse.
                    if (cancelled) {
                        return;
                    }

                    // Parity with the trigger's redirectWithSuppression: mark the URL so a
                    // refresh does not immediately re-fire the same auto-signin. Do this BEFORE
                    // the modal so the URL is stable even if the user closes the modal via the
                    // browser back button rather than the OK button.
                    markPasskeyOfferedInUrl();
                    // SIM-24 LEGACY-PARITY: modal + Adobe Analytics (prop28='passkey:{reason}',
                    // prop48='{errorId} | {message}') fire for EVERY passkey failure — matching
                    // legacy handlePasskeyError coverage (user cancel = 'passkey:biometric', BI SDK
                    // = 'passkey:sdk:authenticate', post-WebAuthn backend = 'passkey:api:login',
                    // pre-WebAuthn API = 'passkey:api:authenticate'). Navigation to credential
                    // entry deferred to the modal's OK-callback so the user acknowledges before
                    // the screen swaps.
                    //
                    // F-012 completion: AWAIT the failure modal so the outer chain's .finally
                    // (which releases passkeySignInLock) does NOT fire until the user dismisses
                    // the modal. Without this the lock releases while the modal is still visible
                    // and a background 401 defer expiring (F-004) could open a competing sign-in
                    // prompt on top. Mirrors the trigger-site Authentication.js SINGLE catch and
                    // openTriggerPasskeySelector's CR-4 pattern.
                    //
                    // Defense-in-depth catch: if the modal helper's dynamic imports (store/Store,
                    // Actions, lazyPasskey) reject — stale-deploy chunk 404, offline — the helper
                    // rejects before dispatching. Invoke goToCredential manually so the user is
                    // not stuck on the passkey-detection screen.
                    try {
                        await showPasskeyFailureModalIfNeeded(error, goToCredential);
                    } catch (modalErr) {
                        goToCredential();
                    }
                })
                .finally(() => {
                    // Single release site (code-review #5): lock spans the inline .then/.catch
                    // work (redirect kick-off / modal dispatch / markUrl), matching Fix 2's
                    // PasskeySelectorModal outer-finally pattern. Release happens after the
                    // handler body returns — before the browser's actual page unload on success,
                    // and before any user-visible modal interaction on failure. ALWAYS runs, even
                    // when `cancelled` is true — leaking the lock across pages would poison the
                    // next page's 401 defer window (ufeApi / newAuthUfeApi).
                    passkeySignInLock.release();
                });
        } else if (passkeyStatus === DETECTION_STATUS.MULTIPLE) {
            hasTriggeredSelector.current = true;
            store.dispatch(
                Actions.showPasskeySelectorModal({
                    isOpen: true,
                    passkeys: detectedPasskeys,
                    // AC-3/AC-4 REDIRECT FIX: replaces the pre-SIM-24 no-op. PasskeySelectorModal
                    // dispatches UserActions.signIn internally (with suppressErrorModal: true) and
                    // calls this callback on success — we honor returnUrl here (ADR-TS-010).
                    successCallback: () => {
                        redirectToReturnUrl();
                    },
                    // AC-4: user closed / dismissed / chose "sign in with another account".
                    // Mark the URL with passkeyOffered=1 (same suppression signal the trigger
                    // site writes when its selector cancels) so a refresh does not re-open the
                    // selector, then fall through to the credential entry screen.
                    errback: () => {
                        markPasskeyOfferedInUrl();
                        goTo('credential');
                    },
                    source: 'signinPage'
                })
            );
        }

        // OURS code-review #8: cleanup flips `cancelled` so any late-arriving Promise handlers
        // in the SINGLE branch above become no-ops for the visible side effects (redirect /
        // URL write / modal). Also observed by THEIRS's NONE-branch async `attemptAutoRoute`
        // so a late lookup response cannot goTo / setAuthContext on an unmounted shell. Runs
        // on true unmount AND on any effect re-run due to deps change. The lock's finally +
        // Dynatrace logging still fire — those must not be skipped. MULTIPLE branch is not
        // tied to this flag because its outcome flows through the Redux-owned selector modal
        // (own lifetime, independent of this component).
        return () => {
            cancelled = true;
        };
    }, [
        currentScreen,
        passkeyStatus,
        detectedPasskeys,
        goTo,
        DETECTION_STATUS,
        passkeyOfferedSuppressed,
        redirectToReturnUrl,
        markPasskeyOfferedInUrl,
        // THEIRS ACS-4437 additions — read by the NONE-branch recognized-user auto-route.
        lookup,
        setAuthContext
    ]);

    return (
        <main
            aria-labelledby={HEADING_ID}
            css={styles.page}
        >
            {/* Site header bar: Sephora logo LEFT-aligned with a light bottom
                shadow (Figma header 700:92764). Fixed gutter: 16px mobile →
                96px desktop (Figma header px-96), matching the footer inset. */}
            <header css={styles.header}>
                <Logo />
            </header>
            {/* Centered content column (400px on desktop) per Figma node 270-84077.
                Field fills the full 400px on desktop (no inner side padding);
                16px gutters only on mobile. */}
            <div css={styles.content}>
                {showShellHeading && (
                    <h1
                        id={HEADING_ID}
                        ref={headingRef}
                        tabIndex={-1}
                        css={styles.heading}
                    >
                        {getText('pageTitle')}
                    </h1>
                )}
                <div
                    data-testid='signin-screen-slot'
                    data-current-screen={currentScreen}
                >
                    {/*
                        TEMP (SIM-2): render CredentialEntryScreen for BOTH the
                        'credential' screen AND the initial 'passkey-detection'
                        screen so /signin lands directly on credential entry until
                        SIM-5 introduces real passkey detection; once SIM-5 ships,
                        'passkey-detection' falls back to the interstice loader and
                        this extra condition is removed. data-current-screen stays
                        bound to the real currentScreen (never hardcoded).
                    */}
                    {(currentScreen === 'credential' || currentScreen === 'passkey-detection') && (
                        <CredentialEntryScreen
                            goTo={goTo}
                            setAuthContext={setAuthContext}
                            /* ACS-4388 (VQA, SIM-7 AC-AUTH-EDIT): when the customer clicks
                               "Edit" on the Authorization/OTP step, they return here and the
                               field must be PRE-FILLED with what they previously entered. The
                               raw value lives in the in-session authContext (never persisted —
                               PII, BR-021), so it is present for this in-memory Edit path. */
                            /* ACS-4388 Edit path AND ACS-4437 (AC-4) registration-redirect
                               carry both seed via `initialCredential` (enteredCredential in
                               authContext). `autoSubmit` (set only on the registration-redirect
                               branch) triggers CredentialEntryScreen's auto-submit — unified
                               with the SIM-17/ACS-4141 contract. */
                            initialCredential={authContext?.enteredCredential}
                            /* ACS-4437 registration-redirect AND SIM-17 (ACS-4141) DEFER-2b
                               account-closure/guest-checkout credential carry both set
                               authContext.autoSubmit to auto-submit the URL-pre-seeded
                               credential on mount, landing on the authorization/OTP step. */
                            autoSubmit={authContext?.autoSubmit}
                        />
                    )}
                    {currentScreen === 'authorization' && authContext && (
                        <AuthorizationScreen
                            goTo={goToFromAuthorization}
                            methods={authContext.availableAuthMethods}
                            securityToken={authContext.securityToken}
                            phoneLast4Digits={authContext.phoneLast4Digits}
                            maskedEmail={authContext.maskedEmail}
                            enteredCredential={authContext.enteredCredential}
                            credentialType={authContext.credentialType}
                            otpSession={otpSession}
                            onSelectMethod={handleSelectMethod}
                            /* SIM-8 / ACS-4132 — inline password state. The screen
                               owns the login call + step-up handoff; SignIn shell
                               just threads the raw credential (from CredentialEntryScreen
                               / F-002) and the SIM-10 completeAuthentication terminal. */
                            credential={authContext.enteredCredential}
                            completeAuthentication={completeAuthentication}
                        />
                    )}
                    {/* SIM-9 (Figma 1466-19076): phone disambiguation. When the
                        lookup returns { requiresEmailConfirmation: true } for an
                        ambiguous phone, CredentialEntryScreen emits goTo('disambiguation');
                        the shell re-renders the SAME screen in email-confirmation mode
                        (copy-only delta). The confirming email re-runs the lookup and,
                        on a single-account result, advances to authorization via the
                        same setAuthContext/goTo seam (no bespoke screen, ADR-SIM9-002). */}
                    {currentScreen === 'disambiguation' && (
                        <CredentialEntryScreen
                            mode='email-confirmation'
                            goTo={goTo}
                            setAuthContext={setAuthContext}
                        />
                    )}
                    {currentScreen === 'otp' && authContext && (
                        <OtpEntryScreen
                            goTo={goTo}
                            method={authContext.mfaMode}
                            session={otpSession}
                            authContext={authContext}
                            completeAuthentication={completeAuthentication}
                            onBusyChange={setBusy}
                            skipInitialSend={otpRestoredRef.current}
                            headingId={HEADING_ID}
                            headingRef={headingRef}
                        />
                    )}
                    {/* SIM-8 / ACS-4132 — forced-challenge OTP step-up. Thin wrapper
                        around OtpEntryScreen; owns its own useOtpSession (does NOT
                        share the outer OTP-first session — the two entry points have
                        distinct step-up semantics and cooldown-carry contracts). */}
                    {currentScreen === 'risk-step-up' && authContext && (
                        <RiskStepUpOtpScreen
                            goTo={goTo}
                            completeAuthentication={completeAuthentication}
                            onBusyChange={setBusy}
                            authContext={authContext}
                        />
                    )}
                </div>
            </div>
            {/* SIM-7 in-flight loader (FIX 3 / FIX 7): a CIRCULAR spinner on a DARK
                25%-black scrim (Figma 270-85083), scoped to the sign-in flow — NOT the
                app's flame loader.gif. Shown while an OTP send/verify is in flight and
                kept up across the post-verify redirect. */}
            {busy && (
                <div css={styles.loaderScrim}>
                    <div
                        css={styles.spinner}
                        role='progressbar'
                        aria-label={getText('loadingLabel')}
                    />
                </div>
            )}
            <LegalFooter />
        </main>
    );
};

// AC-023 / SI-3: Emotion class-scoped color rule on the heading so the
// deterministic stylesheet scan finds a WCAG-AA-safe token color. Heading is
// LEFT-aligned in the content column per Figma node 270-84077 (h3 = 16px).
const styles = {
    // Sticky-footer layout: full-height flex column so the LegalFooter is
    // pushed to the bottom of the viewport when content is short, and scrolls
    // normally when content is tall (same minHeight:100vh precedent as
    // Login.f.jsx / ResetPassword.f.jsx).
    page: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        minHeight: 56,
        // Fixed gutter matching Figma: 16px mobile → 96px desktop (header px-96).
        // NOT a centered max-width Container (that grows the gutter on wide
        // viewports); a fixed inset keeps the logo at 96px like the design.
        paddingLeft: space[4],
        paddingRight: space[4],
        boxShadow: shadows.light, // Figma drop-shadow-light (token; 6px blur ~= design 3px)
        [mediaQueries.md]: {
            paddingLeft: SIDE_GUTTER_DESKTOP,
            paddingRight: SIDE_GUTTER_DESKTOP
        }
    },
    content: {
        flex: 1, // grow to fill space between header and footer (sticky footer)
        width: '100%',
        maxWidth: 400,
        margin: '0 auto',
        // 16px gutters on mobile so the field isn't edge-to-edge; on desktop the
        // 400px column holds the full-width field (Figma form width = 400px).
        paddingLeft: space[4],
        paddingRight: space[4],
        paddingTop: space[6],
        [mediaQueries.md]: {
            paddingLeft: 0,
            paddingRight: 0
        }
    },
    heading: {
        // Figma h3: Helvetica Neue Bold 20px. outline:none suppresses the
        // programmatic-focus box (h1 stays tabIndex=-1 + focused for SI-2;
        // only the visible outline is removed — a11y focus is unchanged).
        color: colors.black,
        fontSize: fontSizes.lg,
        fontWeight: fontWeights.bold,
        marginTop: 0,
        marginBottom: space[4],
        outline: 'none'
    },
    // Fixed full-screen overlay painting the sign-in flow's DARK 25%-black scrim
    // (Figma 270-85083), flex-centering the circular spinner on top. The global
    // Interstice is left untouched (FIX 3).
    loaderScrim: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        // Above the page content (incl. the footer, which renders after this in the
        // DOM) so the busy scrim covers the whole sign-in surface and blocks
        // interaction with footer links while a send/verify is in flight.
        zIndex: 'var(--layer-modal)',
        backgroundColor: colors.darken3
    },
    // Circular spinner (Figma 270-85083) — a rotating ring, NOT the app's flame
    // loader.gif. White ring on the dark scrim; a faint track with a solid top arc.
    spinner: {
        '@keyframes signin-spinner-spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' }
        },
        width: 40,
        height: 40,
        borderRadius: '50%',
        border: '3px solid rgba(255, 255, 255, 0.3)',
        borderTopColor: colors.white,
        animation: 'signin-spinner-spin 0.8s linear infinite'
    }
};

export default wrapFunctionalComponent(SignIn, 'SignIn');
